<template>
  <div v-if="visibleModes.length > 1" class="viewer-mode-toggle" data-testid="viewer-mode-toggle">
    <ContextMenuWrapper placement="bottom-end" test-id="viewer-mode" @open="onMenuOpen" @close="isOpen = false">
      <button
        type="button"
        class="viewer-mode-trigger"
        :class="{ 'viewer-mode-trigger--open': isOpen }"
        :aria-label="triggerAriaLabel"
        :aria-haspopup="'menu'"
        :aria-expanded="isOpen"
        :title="isOpen ? undefined : triggerTooltip"
        data-testid="viewer-mode-trigger"
      >
        <ModeIcon :mode="activeMode" size="sm" class="viewer-mode-trigger__icon" />
        <span class="viewer-mode-trigger__label">{{ activeMode.label }}</span>
        <Icon name="general.chevronDown" size="xs" class="viewer-mode-trigger__chevron" />
      </button>

      <template #menu>
        <li v-for="mode in visibleModes" :key="mode.value" class="viewer-mode-item" :data-testid="`viewer-mode-option-${mode.value}`">
          <button
            type="button"
            class="viewer-mode-item__button"
            :class="{ 'viewer-mode-item__button--active': currentMode === mode.value }"
            role="menuitemradio"
            :aria-checked="currentMode === mode.value"
            @click="onModeClick(mode.value)"
          >
            <span class="viewer-mode-item__left">
              <ModeIcon :mode="mode" size="sm" class="viewer-mode-item__icon" />
              <span>{{ mode.label }}</span>
            </span>
            <Icon v-if="currentMode === mode.value" name="general.tick" size="xs" class="viewer-mode-item__check" />
          </button>
        </li>
        <li v-if="settingsUrl" class="viewer-mode-item viewer-mode-item--divider" data-testid="viewer-mode-settings">
          <a :href="settingsUrl" class="viewer-mode-item__button" role="menuitem" data-testid="viewer-mode-settings-link">
            <span class="viewer-mode-item__left">
              <Icon name="general.cog" size="sm" class="viewer-mode-item__icon" />
              <span>Set default layout</span>
            </span>
          </a>
        </li>
      </template>
    </ContextMenuWrapper>

    <!-- One-time hint pointing new viewers at the mode switcher, since a
         recording can now open in Summary/Logs by default instead of
         Video. Shown on every new recording's first view (once ever
         overall, not per-item), regardless of which mode it opened in —
         dismissed by its own button or by opening the menu at all, either
         of which means it's been seen. -->
    <div v-if="showFirstRunHint" class="viewer-mode-hint" data-testid="viewer-mode-hint" role="status">
      <p class="viewer-mode-hint__text">
        This recording supports multiple views — <strong>{{ hintModeLabels }}</strong
        >. Switch anytime from here.
      </p>
      <button type="button" class="viewer-mode-hint__dismiss" data-testid="viewer-mode-hint-dismiss" @click="dismissFirstRunHint">Got it</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, h, type FunctionalComponent } from 'vue';
import { storeToRefs } from 'pinia';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useStorage } from '@vueuse/core';
import { Icon } from '@/storybook';
import { ContextMenuWrapper } from '@/storybook/menu';
import { useViewerModeStore, ViewerMode } from '../store/useViewerModeStore';
import { useViewerAvailableModes } from '../composables/useViewerAvailableModes';
import { useViewerModeSwitch } from '../composables/useViewerModeSwitch';

interface ModeEntry {
  value: ViewerMode;
  label: string;
  /** `general.*` icon name, or null when `svg` is used instead. */
  icon: string | null;
  /** Inline SVG marker for modes without a matching `general.*` glyph. */
  svg?: 'code';
}

const viewerModeStore = useViewerModeStore();
const { currentMode } = storeToRefs(viewerModeStore);
const { availableModes } = useViewerAvailableModes({ syncCurrentMode: false });
const { switchMode } = useViewerModeSwitch();

// Links to the user's personal default, not this item's mode — the dropdown
// itself only ever changes the current item's mode. Uses a query param rather
// than a `#viewer-preferences` hash: that layout's `data-spy="scroll"`
// (sidebar_layout.html.erb) reacts to any URL fragment on these settings pages
// and strips the server-rendered active nav state shortly after load.
const { id: orgId } = storeToRefs(useOrganizationStore());
const settingsUrl = computed(() => (orgId.value ? `/organizations/${orgId.value}/settings/customize?section=default-layout` : null));

const isOpen = ref(false);

const modeConfig: Record<ViewerMode, ModeEntry> = {
  [ViewerMode.VIDEO]: { value: ViewerMode.VIDEO, label: 'Video', icon: 'general.videoCamera' },
  [ViewerMode.SUMMARY]: { value: ViewerMode.SUMMARY, label: 'Summary', icon: 'general.stars' },
  [ViewerMode.LOGS]: { value: ViewerMode.LOGS, label: 'Logs', icon: null, svg: 'code' },
};

type ModeIconSize = 'xs' | 'sm';
const SIZE_PX: Record<ModeIconSize, number> = { xs: 16, sm: 20 };

/** Renders a storybook `<Icon>` or an inline SVG depending on the mode config. */
const ModeIcon: FunctionalComponent<{ mode: ModeEntry; size?: ModeIconSize }> = props => {
  const size = props.size ?? 'xs';

  if (props.mode.svg === 'code') {
    const dim = SIZE_PX[size];
    return h(
      'svg',
      {
        width: dim,
        height: dim,
        viewBox: '0 0 17 15.3338',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': 2,
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'aria-hidden': 'true',
      },
      [
        // Code brackets `<` `>` with a diagonal slash through them.
        h('path', { d: 'M4.33333 4.33357L1 7.6669L4.33333 11.0002M12.6667 4.33357L16 7.6669L12.6667 11.0002M10.1667 1.00024L6.83333 14.3336' }),
      ]
    );
  }
  if (props.mode.icon) {
    return h(Icon, { name: props.mode.icon, size });
  }
  return null;
};
ModeIcon.props = ['mode', 'size'];

// The menu always renders in this order regardless of `availableModes`
// ordering, so users see a stable Video → Summary → Logs sequence.
const modeOrder: ViewerMode[] = [ViewerMode.VIDEO, ViewerMode.SUMMARY, ViewerMode.LOGS];
const visibleModes = computed(() => modeOrder.filter(m => availableModes.value.includes(m)).map(m => modeConfig[m]));

const activeMode = computed(() => modeConfig[currentMode.value] ?? modeConfig[ViewerMode.VIDEO]);

// Tooltip enumerates all available modes so users discover what else is
// behind the dropdown without having to click it. Suppressed while open.
const triggerTooltip = computed(() => {
  const labels = visibleModes.value.map(m => m.label).join(', ');
  return `View mode — ${labels}`;
});

// Restates the label in plain language for screen readers. "Video" alone
// doesn't convey "this is a switcher"; the full aria-label does.
const triggerAriaLabel = computed(() => `View mode: ${activeMode.value.label}. Click to change.`);

const onModeClick = (mode: ViewerMode) => {
  // Clicking the active row is a deliberate no-op. ContextMenuWrapper closes
  // on any inner click regardless, so the menu still dismisses.
  if (availableModes.value.includes(mode)) {
    switchMode(mode);
  }
};

// Global (not per-item) — once someone's seen this, every item's toggle
// should stay quiet, not just the one they saw it on. Shown regardless of
// which mode the recording opened in — the point is discovering that
// other views exist, not specifically getting back to Video.
const hintModeLabels = computed(() => visibleModes.value.map(m => m.label).join(', '));
const hasSeenFirstRunHint = useStorage('viewer-mode-first-run-hint-seen', false);
const showFirstRunHint = computed(() => !hasSeenFirstRunHint.value && visibleModes.value.length > 1);
const dismissFirstRunHint = () => {
  hasSeenFirstRunHint.value = true;
};
const onMenuOpen = () => {
  isOpen.value = true;
  // Opening the menu at all means they've found the switcher — the hint's
  // job is done even if they didn't click its own dismiss button.
  dismissFirstRunHint();
};
</script>

<style scoped>
.viewer-mode-toggle {
  @apply relative inline-flex items-center;
}

.viewer-mode-hint {
  @apply absolute top-full left-0 mt-2 z-20 flex flex-col gap-2 w-64 p-3 rounded-10 shadow-lg;
  @apply bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900;
}

.viewer-mode-hint__text {
  @apply text-sm leading-snug;
}

.viewer-mode-hint__dismiss {
  @apply self-end text-xs font-bold uppercase tracking-wide text-blue-300 dark:text-blue-700 cursor-pointer hover:underline;
}

.viewer-mode-trigger {
  @apply inline-flex items-center gap-2 h-10 px-3 rounded-10 cursor-pointer transition-colors;
  @apply text-zinc-600 dark:text-zinc-400 bg-transparent;
  @apply hover:bg-zinc-100 dark:hover:bg-zinc-800;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50;
}

.viewer-mode-trigger--open {
  @apply bg-zinc-100 dark:bg-zinc-800;
}

.viewer-mode-trigger__label {
  @apply text-sm font-bold leading-none;
}

.viewer-mode-trigger__chevron {
  @apply transition-transform;
}

.viewer-mode-trigger--open .viewer-mode-trigger__chevron {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .viewer-mode-trigger__chevron {
    transition: none;
  }
}

.viewer-mode-item {
  @apply list-none;
}

.viewer-mode-item--divider {
  @apply mt-1 pt-1 border-t border-zinc-200 dark:border-zinc-700;
}

.viewer-mode-item__button {
  @apply w-full flex items-center justify-between gap-3 px-2 py-2 rounded-sm text-sm font-medium cursor-pointer;
  @apply text-zinc-800 dark:text-zinc-200 bg-transparent;
  @apply hover:bg-zinc-100 dark:hover:bg-zinc-700;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50;
  min-width: 180px;
}

.viewer-mode-item__button--active {
  @apply bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300;
}

.viewer-mode-item__button--active:hover {
  @apply bg-blue-100 dark:bg-blue-900/30;
}

.viewer-mode-item__left {
  @apply flex items-center gap-2 min-w-0;
}

.viewer-mode-item__icon {
  @apply flex-shrink-0;
}

.viewer-mode-item__check {
  @apply flex-shrink-0 text-blue-600 dark:text-blue-400;
}
</style>
